import { useState, useEffect, useCallback } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO } from '../lib/constants';
import { Horizon, Mo, Bd, Dp, Label, Ic } from './shared';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NOTIF_ICONS = {
  bell: (s = 18) => Ic.bell(s, GOLD),
  flame: (s = 18) => Ic.flame(s, EMBER),
  trophy: (s = 18) => Ic.trophy(s, GOLD),
  coin: (s = 18) => Ic.coin(s, GOLD),
  chart: (s = 18) => Ic.chart(s, GOLD),
  payment: (s = 18) => Ic.coin(s, GOLD),
};

// Bell icon with badge for header
export function NotifBell({ count, onClick }) {
  return (
    <button onClick={onClick} data-testid="notif-bell-btn" style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
      {Ic.bell(18, count > 0 ? GOLD : MUTE)}
      {count > 0 && (
        <div data-testid="notif-badge" style={{
          position: 'absolute', top: -2, right: -4, minWidth: 16, height: 16, borderRadius: 8,
          background: EMBER, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px'
        }}>
          <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--light)', lineHeight: 1 }}>{count > 9 ? '9+' : count}</Mo>
        </div>
      )}
    </button>
  );
}

// Full notification inbox screen
export function NotificationInbox({ onClose, onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/notifications/inbox`, { credentials: 'include' });
      const data = await res.json();
      setNotifications(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  const generateReminders = async () => {
    setGenerating(true);
    try {
      await fetch(`${API}/notifications/generate`, { method: 'POST', credentials: 'include' });
      await fetchNotifs();
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const markRead = async (id) => {
    try {
      await fetch(`${API}/notifications/read/${id}`, { method: 'PUT', credentials: 'include' });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, { method: 'PUT', credentials: 'include' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { console.error(e); }
  };

  // Request browser notification permission
  const requestPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        new Notification('Dettio Reminders Active', {
          body: "You'll get nudges before due dates and streak warnings.",
          icon: '🔥'
        });
      }
    }
  };

  const unread = notifications.filter(n => !n.read);
  const read = notifications.filter(n => n.read);

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const sortedUnread = [...unread].sort((a, b) => {
    const pa = priorityOrder[a.priority] !== undefined ? priorityOrder[a.priority] : 2;
    const pb = priorityOrder[b.priority] !== undefined ? priorityOrder[b.priority] : 2;
    if (pa !== pb) return pa - pb;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  const timeSince = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div data-testid="notification-inbox" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        {/* Header actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <button onClick={generateReminders} disabled={generating} data-testid="generate-reminders-btn" style={{
            padding: '8px 12px', background: GOLD, border: 'none', color: 'var(--base)', fontFamily: MONO,
            fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6
          }}>
            {Ic.bell(12, BASE)} {generating ? 'CHECKING...' : 'CHECK REMINDERS'}
          </button>
          {unread.length > 0 && (
            <button onClick={markAllRead} data-testid="mark-all-read-btn" style={{
              padding: '8px 12px', background: 'transparent', border: `1px solid ${GHOST}`, color: 'var(--mute)',
              fontFamily: MONO, fontSize: 9, fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5
            }}>MARK ALL READ</button>
          )}
        </div>

        {/* Browser notification opt-in */}
        {'Notification' in window && Notification.permission === 'default' && (
          <button onClick={requestPermission} data-testid="enable-browser-notifs-btn" style={{
            width: '100%', padding: '14px 16px', marginBottom: 16, background: `${GOLD}08`,
            border: `1px solid ${GOLD}15`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left'
          }}>
            {Ic.bell(18, GOLD)}
            <div>
              <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)' }}>Enable browser notifications</Bd>
              <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block' }}>Get nudges before due dates + streak warnings</Bd>
            </div>
          </button>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Mo className="pulse-a" style={{ fontSize: 12, color: 'var(--mute)' }}>Loading...</Mo></div>
        ) : notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ marginBottom: 12 }}>{Ic.bell(32, GHOST)}</div>
            <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>No notifications yet</Bd>
            <Bd style={{ fontSize: 12, color: 'var(--ghost)', display: 'block', marginTop: 4 }}>Tap "Check Reminders" to generate alerts based on your debts</Bd>
          </div>
        ) : (
          <>
            {sortedUnread.length > 0 && (
              <>
                <Label>NEW ({sortedUnread.length})</Label>
                {sortedUnread.map(n => (
                  <NotifItem key={n.id} notif={n} onRead={() => markRead(n.id)} timeSince={timeSince} onNavigate={onNavigate} onRefresh={fetchNotifs} />
                ))}
              </>
            )}
            {read.length > 0 && (
              <>
                <Label>EARLIER</Label>
                {read.map(n => (
                  <NotifItem key={n.id} notif={n} read timeSince={timeSince} onNavigate={onNavigate} />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function NotifItem({ notif, onRead, read, timeSince, onNavigate, onRefresh }) {
  const [actioning, setActioning] = useState(null); // 'confirm' | 'dismiss'
  const isPaymentAlert = notif.type === 'payment_alert';
  const iconFn = NOTIF_ICONS[notif.icon] || NOTIF_ICONS.bell;
  const accentColor = notif.accent === 'ember' ? EMBER : GOLD;
  const isHigh = notif.priority === 'high';
  const hasAction = !!notif.action;

  const handleAction = async (action) => {
    if (actioning) return;
    setActioning(action);
    try {
      await fetch(`${API}/payment-alerts/${notif.payment_alert_id}/${action}`, {
        method: 'POST', credentials: 'include'
      });
      if (onRefresh) onRefresh();
    } catch (e) { console.error(e); }
    setActioning(null);
  };

  const handleClick = () => {
    if (isPaymentAlert) return; // handled by buttons
    if (onRead) onRead();
    if (hasAction && onNavigate) onNavigate(notif.action);
  };

  return (
    <div data-testid={`notif-item-${notif.id}`} style={{
      padding: '14px 0', borderBottom: `1px solid ${FAINT}`, opacity: read ? 0.5 : 1,
    }}>
      <div onClick={handleClick} style={{
        display: 'flex', gap: 12,
        cursor: (!isPaymentAlert && (onRead || hasAction)) ? 'pointer' : 'default',
      }}>
        <div style={{
          width: 38, height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: read ? FAINT : `${accentColor}12`, border: `1px solid ${read ? GHOST : accentColor}20`
        }}>
          {iconFn(18)}
        </div>
        <div className="dettio-flex-1">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <Bd style={{ fontSize: 13, fontWeight: 600, color: read ? MUTE : LIGHT, flex: 1 }}>{notif.title}</Bd>
            {isHigh && !read && (
              <div style={{ width: 6, height: 6, borderRadius: 3, background: EMBER, flexShrink: 0 }} />
            )}
          </div>
          <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.5, display: 'block' }}>{notif.body}</Bd>
          <Mo style={{ fontSize: 9, color: 'var(--ghost)', marginTop: 4, display: 'block' }}>{timeSince(notif.created_at)}</Mo>
        </div>
      </div>

      {/* Payment alert action buttons */}
      {isPaymentAlert && !read && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, marginLeft: 50 }}>
          <button
            data-testid={`alert-confirm-${notif.payment_alert_id}`}
            onClick={() => handleAction('confirm')}
            disabled={!!actioning}
            style={{
              flex: 1, padding: '10px 0', background: GOLD, border: 'none', borderRadius: 8,
              fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              color: '#000', cursor: 'pointer', opacity: actioning === 'confirm' ? 0.6 : 1,
            }}
          >
            {actioning === 'confirm' ? '...' : 'YES, LOG IT'}
          </button>
          <button
            data-testid={`alert-dismiss-${notif.payment_alert_id}`}
            onClick={() => handleAction('dismiss')}
            disabled={!!actioning}
            style={{
              flex: 1, padding: '10px 0', background: 'transparent', border: `1px solid ${GHOST}`,
              borderRadius: 8, fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              color: MUTE, cursor: 'pointer', opacity: actioning === 'dismiss' ? 0.6 : 1,
            }}
          >
            {actioning === 'dismiss' ? '...' : 'NOT MINE'}
          </button>
        </div>
      )}
    </div>
  );
}

// Toast notification component for real-time alerts
export function NotifToast({ notification, onDismiss }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  if (!notification) return null;
  const accentColor = notification.accent === 'ember' ? EMBER : GOLD;

  return (
    <div data-testid="notif-toast" className="fade-in" style={{
      position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
      width: 'calc(100% - 40px)', maxWidth: 400, zIndex: 300, padding: '14px 16px',
      background: 'var(--surface)', border: `1px solid ${accentColor}30`,
      borderLeft: `3px solid ${accentColor}`, display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: `0 8px 32px rgba(0,0,0,0.6)`
    }}>
      {(NOTIF_ICONS[notification.icon] || NOTIF_ICONS.bell)(16)}
      <div className="dettio-flex-1">
        <Bd style={{ fontSize: 12, fontWeight: 600, color: 'var(--light)' }}>{notification.title}</Bd>
        <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block' }}>{notification.body?.substring(0, 60)}...</Bd>
      </div>
      <button onClick={onDismiss} data-testid="toast-dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
        {Ic.x(14, MUTE)}
      </button>
    </div>
  );
}
